import { useEffect, useRef } from "react";

export const AUTH_CALLBACK_AUTO_RETRY_STORAGE_KEY =
  "ano-auth-callback-auto-retry-at";
export const AUTH_CALLBACK_AUTO_RETRY_WINDOW_MS = 60_000;

/**
 * Pure decision for the `/callback` auto-retry throttle. Given the previously
 * stored retry timestamp (raw string from sessionStorage, or null) and the
 * current time, decide whether a retry should fire and whether the stored
 * timestamp should be refreshed.
 *
 * A retry is suppressed when a prior retry happened within
 * `AUTH_CALLBACK_AUTO_RETRY_WINDOW_MS`; otherwise it fires and the timestamp is
 * stamped to `now`.
 */
export function decideCallbackAutoRetry(
  lastRetryRaw: string | null,
  now: number,
): { shouldRetry: boolean; storeTimestamp: boolean } {
  const lastRetryAt = lastRetryRaw ? Number(lastRetryRaw) : NaN;
  if (
    Number.isFinite(lastRetryAt) &&
    now - lastRetryAt < AUTH_CALLBACK_AUTO_RETRY_WINDOW_MS
  ) {
    return { shouldRetry: false, storeTimestamp: false };
  }
  return { shouldRetry: true, storeTimestamp: true };
}

interface UseCallbackAutoRetryArgs {
  user: unknown;
  isLoading: boolean;
  isAuthActionPending: boolean;
  signIn: () => Promise<void>;
  onError: (message: string) => void;
}

/**
 * When AuthKit lands the OAuth callback at `/callback` but the user object is
 * still null (the exchange silently failed), fire one auto-retry of `signIn()`
 * — throttled to once per `AUTH_CALLBACK_AUTO_RETRY_WINDOW_MS` via
 * sessionStorage so we never loop. Resets the throttle once a user resolves.
 *
 * Extracted verbatim from AuthGate; the dependency array is preserved exactly.
 */
export function useCallbackAutoRetry({
  user,
  isLoading,
  isAuthActionPending,
  signIn,
  onError,
}: UseCallbackAutoRetryArgs): void {
  const callbackRetryAttemptedRef = useRef(false);

  useEffect(() => {
    if (user) {
      callbackRetryAttemptedRef.current = false;
      try {
        window.sessionStorage.removeItem(AUTH_CALLBACK_AUTO_RETRY_STORAGE_KEY);
      } catch {
        // Ignore storage failures
      }
      return;
    }

    if (isLoading || isAuthActionPending) return;
    if (window.location.pathname !== "/callback") return;
    if (callbackRetryAttemptedRef.current) return;

    callbackRetryAttemptedRef.current = true;

    let shouldRetry = true;
    try {
      const now = Date.now();
      const lastRetryRaw = window.sessionStorage.getItem(
        AUTH_CALLBACK_AUTO_RETRY_STORAGE_KEY,
      );
      const decision = decideCallbackAutoRetry(lastRetryRaw, now);
      shouldRetry = decision.shouldRetry;
      if (decision.storeTimestamp) {
        window.sessionStorage.setItem(
          AUTH_CALLBACK_AUTO_RETRY_STORAGE_KEY,
          String(now),
        );
      }
    } catch {
      // Ignore storage failures and proceed with one retry.
    }

    if (!shouldRetry) return;

    void signIn().catch((error) => {
      console.error("Failed to auto-retry sign in after callback:", error);
      onError("Could not finish sign-in callback. Please try again.");
    });
    // `onError` is the caller's stable `setAuthActionError` setter — including
    // it in the deps is a runtime no-op (identity never changes) and preserves
    // the original effect's re-run conditions byte-for-byte. It's listed only
    // to satisfy exhaustive-deps now that it arrives as a prop rather than a
    // statically-detectable useState setter.
  }, [isAuthActionPending, isLoading, signIn, user, onError]);
}
